import { animated, useSpring, useSprings } from '@react-spring/web';
import { CTA } from '@volkswagen-onehub/components-core';
import React, { useEffect, useRef, useState } from 'react';
import { useOfertaCard } from '../../../context/ofertas-card-context';
import {
	CarImageContainer,
	CarImagesContainer,
	CarrouselContainer,
	ControlsContainer,
	IconWrapper,
	ModelCarrouselLayout,
	PauseIcon,
	PlayIcon,
	StyledCarImage,
	StyledCarImagesRow,
} from './ui/model-carrousel-styles';

const CarImagesRow = animated(StyledCarImagesRow);
const CarImage = animated(StyledCarImage);

/* Ordenados por rotacion del vehicle 
[0] front left
[1] side left
[2] back left
[3] back center
[4] back right
[5] side right
[6] front right
[7] front center
*/

const sortedViews = [
	['D6Turntable1', 'Front_Left', 'D6Explore1Alpha'],
	['D6Turntable3', 'Side_Left', 'D6Explore2Alpha'],
	['D6Turntable5', 'Back_Left', 'D6Explore3Alpha'],
	['D6Turntable6', 'Back_Center'],
	['D6Turntable7', 'Back_Right'],
	['D6Turntable9', 'Side_Right'],
	['D6Turntable11', 'Front_Right'],
	['D6Turntable12', 'Front_Center'],
];

export const ModelCarrousel = (): React.ReactElement | null => {
	const { selectedOferta, isShowroom } = useOfertaCard();

	if (!selectedOferta) {
		return null;
	}
	const mediaAssets = selectedOferta.configuracion.mediaAssets;

	// Ordenar cars segun sortedViews para mostrar giro del car (un nodo por índice)
	const sortedKeys = sortedViews
		.map((group) => group.find((k) => mediaAssets && k in mediaAssets))
		.filter((k): k is string => k !== undefined);

	const carImages = mediaAssets
		? [...sortedKeys, ...Object.keys(mediaAssets).filter((k) => !sortedViews.flat().includes(k))]
				.map((k) => mediaAssets[k as keyof typeof mediaAssets])
				.filter((image): image is string => typeof image === 'string' && image.length > 0)
		: [];

	const loopImages = [...carImages, ...carImages, ...carImages];
	const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
	const [imageWidth, setImageWidth] = useState(0);
	const imageRef = useRef<HTMLImageElement | null>(null);
	const [isPaused, setIsPaused] = useState(false);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!imageRef.current || typeof ResizeObserver === 'undefined') return;

		const observer = new ResizeObserver((entries) => {
			const width = entries[0].contentRect.width;
			setImageWidth(width);
		});

		observer.observe(imageRef.current);
		return () => observer.disconnect();
	}, []);

	const [{ x }, carrousel] = useSpring(() => ({ x: 0 }));

	useEffect(() => {
		if (!imageWidth || carImages.length === 0) return;

		const startIndex = carImages.length;
		const initialOffset = -startIndex * imageWidth + (windowWidth / 2 - imageWidth / 2 - 120);
		const targetOffset = initialOffset - imageWidth * carImages.length;
		const distance = imageWidth * carImages.length;
		const duration = distance / 0.04; // velocidad fija (0.04) para todos los carrousels (aunque no tengan el mismo número de CarImages) -> t = d/v

		carrousel.start({
			from: { x: initialOffset },
			to: { x: targetOffset },
			loop: true,
			config: { duration: duration },
		});

		return () => {
			carrousel.stop();
		};
	}, [carrousel, imageWidth, windowWidth, carImages.length]);

	useEffect(() => {
		if (isPaused) {
			carrousel.pause();
		} else {
			carrousel.resume();
		}
	}, [isPaused, carrousel]);

	const menuWidth = 250;
	const center = windowWidth / 2;
	const fadeStart = center - menuWidth - imageWidth / 2;
	const fadeEnd = fadeStart + 150;

	const springs = useSprings(
		loopImages.length,
		loopImages.map((_, i) => ({
			opacity: x.to((val) => {
				const imageCenterX = i * imageWidth + val + imageWidth / 2;
				const distance = center - imageCenterX;
				if (distance < fadeStart) return 1;
				if (distance > fadeEnd) return 0.25;
				const fadeRange = fadeEnd - fadeStart;
				const relative = (distance - fadeStart) / fadeRange;
				return 1 - relative * 0.75;
			}),
		})),
	);

	return carImages.length === 0 ? null : (
		<ModelCarrouselLayout className="model-carrousel__model-carrousel-layout" isShowroom={isShowroom}>
			<CarrouselContainer>
				{carImages.length === 1 ? (
					<CarImageContainer className="model-carrousel__car-image-container">
						<StyledCarImage src={carImages[0]} alt="Imagen del coche" />
					</CarImageContainer>
				) : (
					<>
						<CarImagesContainer className="model-carrousel__car-images-container">
							<CarImagesRow
								style={{
									width: `${loopImages.length * imageWidth}px`,
									transform: x.to((val) => `translateX(${val}px)`),
								}}
							>
								{loopImages.map((image, index) => (
									<CarImage
										key={index}
										ref={index === 0 ? imageRef : null}
										src={image}
										alt={`Imagen del coche ${index + 1}`}
										style={{ opacity: springs[index].opacity }}
									/>
								))}
							</CarImagesRow>
						</CarImagesContainer>

						<ControlsContainer onClick={() => setIsPaused(!isPaused)} className="model-carrousel__controls-container">
							<CTA
								tag="button"
								size="small"
								aria-label={isPaused ? 'Reproducir animación' : 'Detener animación'}
								aria-pressed={isPaused}
								icon={
									isPaused ? (
										<IconWrapper>
											<PlayIcon />
										</IconWrapper>
									) : (
										<IconWrapper>
											<PauseIcon />
										</IconWrapper>
									)
								}
								iconPosition="start"
							>
								<>{isPaused ? 'Reproducir' : 'Detener'}</>
							</CTA>
						</ControlsContainer>
					</>
				)}
			</CarrouselContainer>
		</ModelCarrouselLayout>
	);
};
